import { format, getDayOfYear, isPast, isToday, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { LoaderFunction, useLoaderData, useMatches } from "remix";
import { ButtonLink } from "~/components/Button";
import { ContentBlock } from "~/components/ContentBlock";
import { EventLink } from "~/components/EventLink";
import { ContentBlockModel, getContentBlock } from "~/utils/contentBlocks";
import { useFestival } from "~/utils/useFestival";
import { LoaderData as EventsLoaderData } from "../events";

export interface LoaderData {
  title: ContentBlockModel;
  content: ContentBlockModel;
}

export const loader: LoaderFunction = async ({ params }) => {
  const [title, content] = await Promise.all([
    getContentBlock(`/${params.festivalSlug}/events/title`),
    getContentBlock(`/${params.festivalSlug}/events/content`),
  ]);

  return { title, content };
};

export default function () {
  const { title, content } = useLoaderData<LoaderData>();
  const matches = useMatches();
  const festival = useFestival();
  const eventsLoaderData = matches.find((match) => !!match.data?.events)
    ?.data as EventsLoaderData;

  const isLoggedIn = eventsLoaderData.isLoggedIn;

  let currentDayOfYear: number | null = null;

  const groupedEvents = eventsLoaderData.events.reduce<
    Array<typeof eventsLoaderData.events>
  >((acc, event, index) => {
    const fromDate = parseISO(event.from.toString());

    const thisDayOfYear = getDayOfYear(fromDate);
    const isNewDay = currentDayOfYear !== thisDayOfYear;
    currentDayOfYear = thisDayOfYear;

    if (isNewDay) {
      acc.push([]);
    }
    acc[acc.length - 1].push(event);
    return acc;
  }, []);

  return (
    <>
      <div className="pb-8">
        <h1 className="text-3xl md:text-6xl font-bold my-8">
          <ContentBlock block={title} editable={isLoggedIn} />
        </h1>

        <ContentBlock block={content} editable={isLoggedIn} />
      </div>

      <div className="main-layout-full-bleed bg-gray-50 pt-10 border-t border-t-gray-200 pb-10 overflow-x-auto">
        <div className="main-layout-centered">
          <ul className="md:flex flex-row justify-between gap-6">
            {groupedEvents.map((group) => {
              const fromDate = parseISO(group[0].from.toString());
              const lastToDate = parseISO(group[0].to.toString());

              const dayClasses = isToday(fromDate)
                ? `text-white bg-primary-600`
                : `text-primary-600 bg-primary-50`;

              const inPastClasses = isPast(lastToDate)
                ? " opacity-60 hover:opacity-100"
                : "";

              return (
                <li
                  key={group[0].id}
                  className={`flex-grow flex-shrink-0 basis-0${inPastClasses}`}
                >
                  <ul>
                    <li
                      className={`flex flex-row justify-between px-4 py-2 rounded-md uppercase font-extrabold text-sm ${dayClasses}`}
                    >
                      <span>{format(fromDate, "EEEE", { locale: de })}</span>
                      <span className={`opacity-90 ${dayClasses}`}>
                        {format(fromDate, "P", { locale: de })}
                      </span>
                    </li>
                    {group.map((event) => {
                      return (
                        <li key={event.id}>
                          <EventLink event={event} />
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            })}
          </ul>
          {!!isLoggedIn && (
            <div className="my-8">
              {!!festival && (
                <ButtonLink to={`/${festival.slug}/events/new`} className="">
                  Add new Event
                </ButtonLink>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
